import {FunctionComponent, useCallback, useEffect, useState} from 'react';
import {app, useForm} from 'b302-frontend-library';

import Country from '../../../../networking/models/Country';

import FilterSection from './FilterSection';
import HanCheckbox from '../../../utils/HanCheckbox';
import HanInput from '../../../utils/HanInput';
import Slider, {SliderChangedEvent} from '../../../layout/Slider';
import HanButton from '../../../utils/HanButton';
import {RESET_UNIVERSITY_FILTER} from '../../../../utils/Events';
import {FV} from '../../../../utils/Validation';
import UniversityFilterSlider from './UniversityFilterSlider';

export interface FilterChangeEvent {
    countries: number[];
    minCosts: number;
    maxCosts: number;
    reviewScore: number;
    activeFilters: number;
    isReset: boolean;
}

interface PrefilledFilterValues {
    minCosts?: number,
    maxCosts?: number,
    reviewScore?: number,
    selectedCountries?: number[],
}

interface Props {
    countries: Country[];
    onFilterChange: (e: FilterChangeEvent) => void;
    prefilledFilterValues?: PrefilledFilterValues;
}

const defaultFilterValues = {
    minCosts: 0,
    maxCosts: 3000,
    reviewScore: 0,
    selectedCountries: []
};

const UniversityFilter: FunctionComponent<Props> = ({countries, onFilterChange, prefilledFilterValues}) => {

    const [filters, setFilters] = useState<FilterChangeEvent>({
        countries: prefilledFilterValues?.selectedCountries ?? defaultFilterValues.selectedCountries,
        minCosts: prefilledFilterValues?.minCosts ?? defaultFilterValues.minCosts,
        maxCosts: prefilledFilterValues?.maxCosts ?? defaultFilterValues.maxCosts,
        reviewScore: prefilledFilterValues?.reviewScore ?? defaultFilterValues.reviewScore,
        activeFilters: 0,
        isReset: false
    });

    const [costSlider] = useForm<{
        minCosts: string;
        maxCosts: string;
    }>(f => ({
        minCosts: f.input(filters.minCosts.toString())
            .cValidations([FV.range(0, 3000)]),

        maxCosts: f.input(filters.maxCosts.toString())
            .cValidations([FV.range(0, 3000)]),
    }));

    const getActiveFilterCount = () => {
        let count = 0;

        if (filters.minCosts !== defaultFilterValues.minCosts) {
            count++;
        }

        if (filters.maxCosts !== defaultFilterValues.maxCosts) {
            count++;
        }

        if (filters.reviewScore !== defaultFilterValues.reviewScore) {
            count++;
        }

        if (filters.countries.length !== defaultFilterValues.selectedCountries?.length) {
            count++;
        }

        return count;
    }
    useCallback(() => {
        return onFilterChange({
            countries: filters.countries,
            reviewScore: filters.reviewScore,
            minCosts: filters.minCosts,
            maxCosts: filters.maxCosts,
            activeFilters: getActiveFilterCount(),
            isReset: false
        });
    }, [setFilters]);

    useEffect(() => {
        return onFilterChange({
            countries: filters.countries,
            reviewScore: filters.reviewScore,
            minCosts: filters.minCosts,
            maxCosts: filters.maxCosts,
            activeFilters: getActiveFilterCount(),
            isReset: false
        });
    }, [filters, setFilters]);

    const resetFilter = () => {
        setFilters({
            countries: defaultFilterValues.selectedCountries,
            minCosts: defaultFilterValues.minCosts,
            maxCosts: defaultFilterValues.maxCosts,
            reviewScore: defaultFilterValues.reviewScore,
            activeFilters: 0,
            isReset: true
        });

        costSlider.set({
            maxCosts: defaultFilterValues.maxCosts.toString(),
            minCosts: defaultFilterValues.minCosts.toString()
        });

        onFilterChange({
            minCosts: defaultFilterValues.minCosts,
            maxCosts: defaultFilterValues.maxCosts,
            countries: defaultFilterValues.selectedCountries,
            reviewScore: defaultFilterValues.reviewScore,
            activeFilters: 0,
            isReset: true
        });
    };

    useEffect(() => {
        app().onEvent(RESET_UNIVERSITY_FILTER, resetFilter);

        return () => {
            app().removeEvent(RESET_UNIVERSITY_FILTER, resetFilter);
        };
    }, [resetFilter]);

    const toggleCountry = (id: number) => {
        const newCountries = [...filters.countries];
        const index = newCountries.findIndex(country => country === id);

        if (index === -1) {
            newCountries.push(id);
        } else {
            newCountries.splice(index, 1);
        }

        setFilters({
            ...filters,
            countries: newCountries,
        })
    };

    const costInputChanged = (e: any) => {
        const value = parseInt(e.value) || 0;
        if (e.id! === "minCosts") {

            if (value < defaultFilterValues.minCosts) {
                setFilters({
                    ...filters,
                    minCosts: defaultFilterValues.minCosts
                });
            }

            if (value > defaultFilterValues.maxCosts) {
                setFilters({
                    ...filters,
                    minCosts: defaultFilterValues.minCosts
                });
            }

            if (value > filters.maxCosts) {
                setFilters({
                    ...filters,
                    maxCosts: value
                });
            }

            costSlider.set({minCosts: value.toString()});
        } else if (e.id! === "maxCosts") {

            if (value < defaultFilterValues.minCosts) {
                setFilters({
                    ...filters,
                    maxCosts: defaultFilterValues.minCosts
                });
            }

            if (value > defaultFilterValues.maxCosts) {
                setFilters({
                    ...filters,
                    maxCosts: defaultFilterValues.maxCosts
                });
            }

            if (value < filters.minCosts) {
                setFilters({
                    ...filters,
                    minCosts: value
                });
            }

            costSlider.set({maxCosts: value.toString()});
        }
    }

    const minMaxCostSliderChanged = (e: SliderChangedEvent) => {
        for (let i = 0; i < e.ids!.length; i++) {
            const id = e.ids![i];

            if (id === "minCosts") {
                setFilters({
                    ...filters,
                    minCosts: e.values[i]
                });
                costSlider.set({minCosts: e.values[i].toString()});
            } else if (id === "maxCosts") {
                setFilters({
                    ...filters,
                    maxCosts: e.values[i]
                });
                costSlider.set({maxCosts: e.values[i].toString()});
            }
        }
    }

    const filtersAreActive = (): boolean => {
        return getActiveFilterCount() > 0;
    };

    return (
        <div className={'university-filter'}>
            <FilterSection title={`Countries (${countries.length})`}>
                {countries.map(country => (
                    <div className={'filter__checkbox'} key={country.id}>
                        <HanCheckbox
                            onClick={() => toggleCountry(country.id!)}
                            checked={filters.countries.includes(country.id!)}/>

                        <span className={'university-filter__text'}>{country.name}</span>
                    </div>
                ))}
            </FilterSection>

            <FilterSection title={'Cost of living'}>
                <UniversityFilterSlider
                    form={costSlider}
                    min={defaultFilterValues.minCosts}
                    max={defaultFilterValues.maxCosts}
                    onInputChange={costInputChanged}
                    onChange={minMaxCostSliderChanged} />

                {/*<div className={'university-filter__center-space'}>*/}
                {/*    <HanInput*/}
                {/*        form={costSlider}*/}
                {/*        onChange={costInputChanged}*/}
                {/*        className={'university-filter__input'}*/}
                {/*        id={'minCosts'}/>*/}

                {/*    <HanInput*/}
                {/*        id={'maxCosts'}*/}
                {/*        form={costSlider}*/}
                {/*        onChange={costInputChanged}*/}
                {/*        className={'university-filter__input'}*/}
                {/*    />*/}
                {/*</div>*/}

                {/*<div className={'university-filter__slider-container'}>*/}
                {/*    <Slider*/}
                {/*        min={defaultFilterValues.minCosts}*/}
                {/*        max={defaultFilterValues.maxCosts}*/}
                {/*        value={[costSlider.values.minCosts, costSlider.values.maxCosts]}*/}
                {/*        onAfterChange={minMaxCostSliderChanged}*/}
                {/*        pearling={true}*/}
                {/*        ids={['minCosts', 'maxCosts']}/>*/}
                {/*</div>*/}
            </FilterSection>

            {/* <FilterSection title={'Review score'}>
                <div className={'university-filter__center-space'}>
                    {/* <Rating
                        size={'large'}
                        rating={filters.reviewScore}
                        onChange={(value) => {
                            setFilters({
                                ...filters,
                                reviewScore: value === filters.reviewScore ? defaultFilterValues.reviewScore : value
                            });
                        }}/>}
                    <span className='university-filter__and-up'>& up</span>
                </div>
            </FilterSection> */}

            {filtersAreActive() &&
                <div className={'university-filter__reset-button--bottom'}>
                    <HanButton onClick={resetFilter}>Remove filters</HanButton>
                </div>
            }
        </div>
    );
};

export default UniversityFilter;


